import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import Mountable from "@/components/styled/div/Mountable";
import { themeColors } from "@/utils/themeColors";
import { FlashcardSet, ItemType } from "@knowt/syncing/graphql/schema";
import { UNTITLED } from "@knowt/syncing/utils/dataCleaning";
import { SetOverviewDue, SetOverviewRestrictionBox } from "./RestrictionUIs";
import StarRating from "@/components/StarRating";
import { useFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import { MoveToPopupProvider } from "@/features/MoveToPopup/hooks/useMoveToPopupContext";
import FlashcardSetOptionsMenu from "@/components/cards/UserContentCard/components/FlashcardSetOptionsMenu";
import SetOverviewRatingBtn from "./SetOverviewRatingBtn";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { ChartNoAxesColumnIncreasing, Pencil } from "lucide-react";
import { strokeWidth } from "@/utils/iconProps";
import LinkWrapper from "@/components/wrappers/Link";
import { SetOverviewViewStates } from "../types";
import { ClassDashboardTab } from "@/features/UserContentManagement/ClassPage/types";
import { useRouter } from "@bprogress/next";
import { ASSETS_URL } from "@/config/deployConstants";
import Image from "next/image";
import { spacing } from "@/utils/spacing";
import br from "@/styles/breakpoints.module.css";
import clsx from "clsx";

const ViewStatsBtn = ({ flashcardSet }: { flashcardSet: FlashcardSet }) => {
    const router = useRouter();
    const href = `/class/${flashcardSet.classId}/dashboard?tab=${ClassDashboardTab.MATERIALS}&itemId=${flashcardSet.flashcardSetId}&itemType=${ItemType.FLASHCARDSET}`;

    return (
        <CircularRectTextButton
            onClick={() => router.push(href)}
            className="secondaryTextBold1"
            sx={{
                padding: "0 1.6rem",
                height: "4rem",
                gap: "0.8rem",
                backgroundColor: themeColors.primary,
                color: themeColors.primaryDark,
            }}>
            <ChartNoAxesColumnIncreasing size="20" strokeWidth={strokeWidth.bold} /> View Stats
        </CircularRectTextButton>
    );
};

const ViewLinkedNotesBtn = ({ flashcardSet }: { flashcardSet: FlashcardSet }) => {
    const href = `/note/${flashcardSet.noteId}`;
    return (
        <LinkWrapper href={href}>
            <CircularRectTextButton
                component="div"
                className="secondaryTextBold1"
                sx={{
                    paddingInline: spacing.SM,
                    height: "4rem",
                    gap: "0.8rem",
                    border: `1px solid ${themeColors.neutral1}`,
                    whiteSpace: "nowrap",
                }}>
                <Image src={`${ASSETS_URL}/images/note-icon.svg`} alt="linked notes" width={20} height={20} />
                View linked note
            </CircularRectTextButton>
        </LinkWrapper>
    );
};

const EditFlashcardBtn = ({ flashcardSet }: { flashcardSet: FlashcardSet }) => {
    const href = `/flashcards/${flashcardSet.flashcardSetId}/edit`;
    return (
        <LinkWrapper href={href}>
            <CircularRectTextButton
                component="div"
                className="secondaryTextBold1"
                sx={{
                    width: "16.2rem",
                    height: "4rem",
                    gap: "0.8rem",
                    border: `1px solid ${themeColors.neutral1}`,
                }}>
                <Pencil size="20" strokeWidth={strokeWidth.normal} /> Edit flashcards
            </CircularRectTextButton>
        </LinkWrapper>
    );
};

const RatingRow = ({ flashcardSet, readOnly }: { flashcardSet: FlashcardSet; readOnly: boolean }) => {
    const ratingStats = { rating: flashcardSet?.rating || 0, count: flashcardSet?.ratingCount || 0 };

    if (!flashcardSet) return null;

    return (
        <FlexRowAlignCenter style={{ gap: "1rem" }}>
            <span
                className="secondaryTextBold2"
                data-testid="auto-id-79cb49b7-8c3f-4f57-a5ff-4e0ae76da52e">{ratingStats.rating.toFixed(1)}</span>
            <StarRating readOnly sx={{ fontSize: "1.8rem" }} value={ratingStats.rating} />
            <span
                className="secondaryText2"
                data-testid="auto-id-1102c28b-b6a7-4a58-a559-416ff617a233">({ratingStats.count})</span>
            {readOnly && <SetOverviewRatingBtn flashcardSet={flashcardSet} readOnly={readOnly} />}
        </FlexRowAlignCenter>
    );
};

const SetOverviewHeader = ({
    flashcardSet: initialFlashcardSet,
    setViewStates,
}: {
    flashcardSet: FlashcardSet;
    setViewStates: SetOverviewViewStates;
}) => {
    const { flashcardSet: flashcardSet_ } = useFlashcardSet({
        flashcardSetId: initialFlashcardSet.flashcardSetId,
        fallbackData: initialFlashcardSet,
    });

    const flashcardSet = flashcardSet_ || initialFlashcardSet;

    const hasRestricted = (flashcardSet?.studySettings?.blockedStudyModes?.length ?? 0) > 0;

    return (
        <FlexColumn
            style={{ padding: "3rem", backgroundColor: themeColors.neutralWhite, borderRadius: "3rem", gap: "1rem" }}>
            <FlexRowAlignCenter style={{ justifyContent: "space-between", textAlign: "center" }}>
                <FlexColumn
                    style={{
                        gap: spacing.SM,
                    }}>
                    <h1
                        className="clampText heading4"
                        style={{
                            textAlign: "left",
                        }}
                        data-testid="auto-id-94d52f98-025b-4837-ab29-9c14a1338806">
                        {flashcardSet?.title || UNTITLED}
                    </h1>
                    <Mountable mount={!!flashcardSet?.deadline}>
                        <SetOverviewDue deadline={flashcardSet?.deadline} />
                    </Mountable>
                    <Mountable mount={!setViewStates.isClassSet}>
                        <FlexRowAlignCenter
                            style={{ gap: "1rem" }}
                            className={clsx(br.smDownColumn, br.smDownAlignStart)}>
                            <RatingRow flashcardSet={flashcardSet} readOnly={setViewStates.isReadOnly} />

                            <FlexRowAlignCenter>
                                <Image
                                    src={`${ASSETS_URL}/images/views-icon.svg`}
                                    alt={"studied by"}
                                    width={25}
                                    height={25}
                                    style={{ objectFit: "contain" }}
                                />
                                Studied by {flashcardSet?.views ?? 0}{" "}
                                {(flashcardSet?.views ?? 0) === 1 ? "person" : "people"}
                            </FlexRowAlignCenter>
                        </FlexRowAlignCenter>
                    </Mountable>
                </FlexColumn>

                <FlexRowAlignCenter style={{ gap: "1.2rem" }}>
                    <Mountable mount={setViewStates.isClassSet && setViewStates.isReadOnly}>
                        <RatingRow flashcardSet={flashcardSet} readOnly={setViewStates.isReadOnly} />
                    </Mountable>

                    <Mountable mount={!!flashcardSet?.noteId}>
                        <ViewLinkedNotesBtn flashcardSet={flashcardSet} />
                    </Mountable>

                    <Mountable mount={!setViewStates.isReadOnly}>
                        <EditFlashcardBtn flashcardSet={flashcardSet || initialFlashcardSet} />
                    </Mountable>

                    <Mountable mount={setViewStates.isTeacherViewClassSet}>
                        <ViewStatsBtn flashcardSet={flashcardSet} />
                    </Mountable>
                    <MoveToPopupProvider>
                        <FlashcardSetOptionsMenu
                            flashcardSet={flashcardSet}
                            btnSx={{
                                height: "4rem",
                                width: "4rem",
                                border: `1px solid ${themeColors.neutral1}`,
                                transform: "rotate(90deg)",
                            }}
                        />
                    </MoveToPopupProvider>
                </FlexRowAlignCenter>
            </FlexRowAlignCenter>
            <Mountable mount={hasRestricted}>
                <SetOverviewRestrictionBox />
            </Mountable>
        </FlexColumn>
    );
};

export default SetOverviewHeader;
